/**
 * Strip row values out of a database error message before it becomes prompt text.
 *
 * `db_run_sql` never hands the model a row. But a failed query comes back as the
 * driver's error string, and some of those quote row data. MySQL 1062 says
 * "Duplicate entry 'alice@example.com' for key 'users.email'" even when the
 * statement was `UPDATE users SET email = LOWER(email)`, so the value came from a
 * row and not from the model. Postgres DETAIL lines ("Key (email)=(x) already
 * exists", "Failing row contains (...)") and casts ("invalid input syntax for type
 * integer: \"abc\"") do the same.
 *
 * The rule: a quoted literal survives only if the model already has it, meaning it
 * appears as a whole token in the SQL it sent (case-insensitively; that covers
 * identifiers such as "Unknown column 'emial'"), or it sits right after a schema
 * word (key, constraint, column…) and so names schema, not data. Everything else
 * becomes <value>. The error number and code are never touched, so the model can
 * still diagnose.
 *
 * Callers: AIChatPanel condenseToolResultForApi (db_run_sql errors) and
 * failurePrompts buildSqlErrorPrompt (the DB grid's "Explain this error").
 */

const VALUE = '<value>';

// A quoted literal: '…' with '' or \' escapes, or "…" with "" escapes.
const QUOTED = /'(?:[^'\\]|\\.|'')*'|"(?:[^"\\]|\\.|"")*"/g;

// Schema words: a literal right after one of these names schema, not data.
const SCHEMA_WORD = /\b(?:key|constraint|index|table|relation|column|database|schema|function|type|sequence|view|trigger)\s*$/i;

// MySQL's clause names in "Unknown column 'x' in 'field list'".
const CLAUSE_NAME = /^[a-z ]+ (?:list|clause)$/i;

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Whole-token match, so a '1' from a row is not "known" because the SQL says LIMIT 10. */
function knownToModel(inner, sqlLower) {
  if (!inner) return true;
  const token = new RegExp(`(^|[^a-z0-9_])${escapeRe(inner.toLowerCase())}($|[^a-z0-9_])`);
  return token.test(sqlLower);
}

export function scrubDbErrorValues(message, sql = '') {
  if (typeof message !== 'string' || !message) return message;
  const sqlLower = String(sql || '').toLowerCase();

  let out = message
    // Postgres DETAIL: Key (email)=(alice@example.com) already exists.
    .replace(/(\bKey \([^)]*\)=\()(.*?)(\)(?:\s|$|\.))/g, (m, head, vals, tail) =>
      (knownToModel(vals, sqlLower) ? m : `${head}${VALUE}${tail}`))
    // Postgres DETAIL: Failing row contains (1, alice@example.com, null).
    .replace(/(Failing row contains \()(.*)(\))/g, (m, head, vals, tail) =>
      (knownToModel(vals, sqlLower) ? m : `${head}${VALUE}${tail}`));

  out = out.replace(QUOTED, (lit, offset, whole) => {
    const inner = lit.slice(1, -1);
    if (inner === VALUE || knownToModel(inner, sqlLower)) return lit;
    if (CLAUSE_NAME.test(inner)) return lit;
    if (SCHEMA_WORD.test(whole.slice(Math.max(0, offset - 24), offset))) return lit;
    return `${lit[0]}${VALUE}${lit[0]}`;
  });
  return out;
}
